/**
 * @fileoverview
 * Create Audit Context Service - Resolves header selections + geolocation
 * for creating a new audit without the start-audit modal.
 *
 * @version 1.0.0
 * @author KATALYST TEAM
 */

import { Injectable, inject } from '@angular/core';
import { GeolocationService } from './geolocation.service';
import { NotificationService } from './notification.service';
import { SelectedAuditTypeService } from './selected-audit-type.service';
import { SelectedBranchService } from './selected-branch.service';

export interface CreateAuditContext {
  auditType: string;
  regionId: number;
  branchId: number;
  geoLat: number;
  geoLng: number;
  deviceInfo: {
    userAgent: string;
    platform: string;
    deviceId?: string;
    version?: string;
    [key: string]: any;
  };
}

/** Header branch selection for entry-master create (no geolocation). */
export interface EntryMasterBranchContext {
  regionId: number;
  branchId: number;
  branchName: string;
}

@Injectable({
  providedIn: 'root',
})
export class CreateAuditContextService {
  private readonly selectedAuditTypeService = inject(SelectedAuditTypeService);
  private readonly selectedBranchService = inject(SelectedBranchService);
  private readonly geolocationService = inject(GeolocationService);
  private readonly notificationService = inject(NotificationService);

  /**
   * Resolve region/branch from the global header selection (no geolocation).
   * Used by Asset Entry / Vault & Key Entry / Key Movement Sender create flows.
   */
  resolveBranchSelection(): EntryMasterBranchContext {
    const branch = this.selectedBranchService.selectedBranch();

    if (!branch?.id || branch.regionId == null) {
      const message = 'Please select a branch from the header before starting.';
      this.notificationService.error(message);
      throw new Error(message);
    }

    return {
      regionId: branch.regionId,
      branchId: branch.id,
      branchName: branch.label,
    };
  }

  /**
   * Resolve create-audit payload fields from global header state and live geolocation.
   */
  async resolve(): Promise<CreateAuditContext> {
    const auditType = this.selectedAuditTypeService.selectedAuditType();
    const branch = this.resolveBranchSelection();

    this.notificationService.info('Getting your location... This may take up to 30 seconds.');

    try {
      const position = await this.geolocationService.getCurrentPosition();
      return {
        auditType,
        regionId: branch.regionId,
        branchId: branch.branchId,
        geoLat: position.latitude,
        geoLng: position.longitude,
        deviceInfo: this.collectDeviceInfo(),
      };
    } catch (err: unknown) {
      const geoError = err as Parameters<GeolocationService['getErrorMessage']>[0];
      const errorMessage = this.geolocationService.getErrorMessage(geoError);
      this.notificationService.error(errorMessage);
      throw err instanceof Error ? err : new Error(errorMessage);
    }
  }

  private collectDeviceInfo(): CreateAuditContext['deviceInfo'] {
    return {
      userAgent: navigator.userAgent,
      platform: this.getDevicePlatform(),
      deviceId: this.getDeviceId(),
      version: this.getDeviceVersion(),
    };
  }

  private getDevicePlatform(): string {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/android/.test(userAgent)) {
      return 'android';
    } else if (/iphone|ipad|ipod/.test(userAgent)) {
      return 'ios';
    } else if (/windows/.test(userAgent)) {
      return 'windows';
    } else if (/mac/.test(userAgent)) {
      return 'macos';
    } else if (/linux/.test(userAgent)) {
      return 'linux';
    }
    return navigator.platform || 'unknown';
  }

  private getDeviceId(): string {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillText('Device fingerprint', 2, 2);
      }
      const fingerprint = [
        navigator.userAgent,
        navigator.language,
        screen.width + 'x' + screen.height,
        new Date().getTimezoneOffset(),
        canvas.toDataURL(),
      ].join('|');

      let hash = 0;
      for (let i = 0; i < fingerprint.length; i++) {
        const char = fingerprint.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash;
      }
      deviceId = `device-${Math.abs(hash)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  private getDeviceVersion(): string {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/(?:Android|iPhone OS|Windows|Mac OS X|Linux)[\s\/]?([\d.]+)/i);
    return match ? match[1] : 'unknown';
  }
}
